import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { 
  Users, Vote, PlusCircle, Search, MapPin, Share2, 
  ThumbsUp, Check, GraduationCap, Briefcase, 
  Filter, Sparkles, Lock, LogIn, ChevronRight, Award,
  Phone, MessageCircle, X
} from 'lucide-react';
import SEO from '../components/SEO';
import PageLoader from '../components/PageLoader';
import ApplyBhaviSarpanchModal from '../components/ApplyBhaviSarpanchModal';
import api from '../utils/api';
import { isUserLoggedIn } from '../utils/auth';
import { showSuccess, showError } from '../utils/toast';

const FATEHABAD_BLOCKS = [
  'All Blocks',
  'Fatehabad',
  'Ratia',
  'Tohana',
  'Bhattukalan',
  'Bhuna',
  'Jakhal',
  'Nagpur',
];

const getPhotoUrl = (photo) => {
  if (!photo) return '';
  if (photo.startsWith('http://') || photo.startsWith('https://')) return photo;
  const backendBase = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
    ? 'http://localhost:5000'
    : (process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '') : '');
  return `${backendBase}${photo.startsWith('/') ? photo : `/${photo}`}`;
};

export default function BhaviSarpanchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const villageParam = searchParams.get('village') || '';
  const blockParam = searchParams.get('block') || '';

  const [candidates, setCandidates] = useState([]);
  const [villages, setVillages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [votingId, setVotingId] = useState(null);

  // Filters
  const [selectedBlock, setSelectedBlock] = useState(blockParam || 'All Blocks');
  const [selectedVillage, setSelectedVillage] = useState(villageParam || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('votes');

  // Modals
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginTargetName, setLoginTargetName] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  useEffect(() => {
    fetchVillages();
  }, []);

  useEffect(() => {
    fetchCandidates();
  }, [selectedBlock, selectedVillage, sortBy]);

  const fetchVillages = async () => {
    try {
      const res = await api.get('/villages');
      const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      setVillages(list);
    } catch (err) {
      console.error('Error fetching villages:', err);
    }
  };

  const handleBlockChange = (newBlock) => {
    setSelectedBlock(newBlock);
    setSelectedVillage('');
    const newParams = new URLSearchParams(searchParams);
    if (newBlock && newBlock !== 'All Blocks') {
      newParams.set('block', newBlock);
    } else {
      newParams.delete('block');
    }
    newParams.delete('village');
    setSearchParams(newParams);
  };

  const handleVillageChange = (newVillage) => {
    setSelectedVillage(newVillage);
    const newParams = new URLSearchParams(searchParams);
    if (newVillage) {
      newParams.set('village', newVillage);
    } else {
      newParams.delete('village');
    }
    setSearchParams(newParams);
  };

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      let url = `/bhavi-sarpanch?sort=${sortBy}`;
      if (selectedBlock && selectedBlock !== 'All Blocks') {
        url += `&block=${encodeURIComponent(selectedBlock)}`;
      }
      if (selectedVillage) {
        url += `&villageSlug=${encodeURIComponent(selectedVillage)}`;
      }

      const res = await api.get(url);
      if (res.data?.success) {
        setCandidates(res.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching candidates:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter candidates locally by selected block, village, and search query
  const filteredCandidates = useMemo(() => {
    return candidates.filter((c) => {
      // Block match
      const normSelectedBlock =
        selectedBlock && selectedBlock !== 'All Blocks'
          ? selectedBlock.toLowerCase().replace(/\s+/g, '')
          : '';
      const cBlockNorm = (c.block || c.village?.block || '').toLowerCase().replace(/\s+/g, '');
      const matchBlock = !normSelectedBlock || cBlockNorm === normSelectedBlock;

      // Village match
      const matchVillage =
        !selectedVillage ||
        c.village?.slug === selectedVillage ||
        c.village?._id === selectedVillage;

      // Search match
      const q = searchQuery.toLowerCase().trim();
      const matchQuery =
        !q ||
        c.name?.toLowerCase().includes(q) ||
        c.village?.name?.toLowerCase().includes(q) ||
        c.block?.toLowerCase().includes(q) ||
        c.vision?.toLowerCase().includes(q) ||
        c.occupation?.toLowerCase().includes(q);

      return matchBlock && matchVillage && matchQuery;
    });
  }, [candidates, selectedBlock, selectedVillage, searchQuery]);

  // Handle Voting
  const handleVote = async (candidate) => {
    if (!isUserLoggedIn()) {
      setLoginTargetName(candidate.name);
      setShowLoginModal(true);
      return;
    }

    try {
      setVotingId(candidate._id);
      const res = await api.post(`/bhavi-sarpanch/${candidate._id}/vote`);

      if (res.data?.success) {
        // Optimistically update candidate in state
        setCandidates((prev) =>
          prev.map((c) =>
            c._id === candidate._id
              ? {
                  ...c,
                  votesCount: res.data.votesCount,
                  userVoted: res.data.userVoted,
                }
              : c
          )
        );
        setSelectedCandidate((prev) =>
          prev && prev._id === candidate._id
            ? {
                ...prev,
                votesCount: res.data.votesCount,
                userVoted: res.data.userVoted,
              }
            : prev
        );
        showSuccess(res.data.message);
      }
    } catch (err) {
      const errMsg = err.response?.data?.message || 'वोट दर्ज करने में त्रुटि हुई।';
      showError(errMsg);
    } finally {
      setVotingId(null);
    }
  };

  // Handle WhatsApp Share (Crisp & minimal text)
  const handleWhatsAppShare = (candidate) => {
    const villageName = candidate.village?.name || candidate.block || '';
    const shareUrl = `${window.location.origin}/bhavi-sarpanch?village=${candidate.village?.slug || ''}`;
    const text = encodeURIComponent(
      `🗳️ *भावी सरपंच प्रत्याशी: ${candidate.name}* (गाँव ${villageName})\n\n👉 *वोट दें व प्रोफाइल देखें:*\n${shareUrl}`
    );
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  // Dynamic available blocks from villages
  const availableBlocks = useMemo(() => {
    const set = new Set();
    villages.forEach((v) => {
      if (v.block && v.block.trim()) {
        set.add(v.block.trim());
      }
    });
    const blockList = Array.from(set).sort();
    return blockList.length > 0 ? ['All Blocks', ...blockList] : FATEHABAD_BLOCKS;
  }, [villages]);

  // Filter village dropdown options by selected block
  const blockVillages = useMemo(() => {
    if (!selectedBlock || selectedBlock === 'All Blocks') return villages;
    const normSelected = selectedBlock.toLowerCase().replace(/\s+/g, '');
    return villages.filter((v) => {
      const normV = (v.block || '').toLowerCase().replace(/\s+/g, '');
      return normV === normSelected;
    });
  }, [villages, selectedBlock]);

  // Total votes aggregate
  const totalVotesAcrossAll = useMemo(() => {
    return candidates.reduce((sum, c) => sum + (c.votesCount || 0), 0);
  }, [candidates]);

  return (
    <>
      <SEO
        title="गाँव के भावी सरपंच (Bhavi Sarpanch) | Fatehabad District Candidate Directory & Opinion Poll"
        description="फतेहाबाद जिले के सभी गाँवों के भावी सरपंच प्रत्याशियों की सूची, विज़न, चुनावी घोषणापत्र और जनमत समर्थन पोल देखें। अपने पसंदीदा प्रत्याशी को वोट दें।"
        canonical="/bhavi-sarpanch"
      />

      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pb-20">
        
        {/* HERO BANNER - Deep Dark Theme */}
        <div className="relative bg-gray-950 text-white py-12 md:py-16 px-4 sm:px-6 lg:px-8 border-b border-gray-800/80 shadow-2xl overflow-hidden">
          {/* Ambient Glows */}
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/90 via-gray-950 to-gray-950 pointer-events-none" />
          <div className="absolute -top-24 right-0 w-96 h-96 bg-amber-600/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 left-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />

          <div className="max-w-7xl mx-auto relative z-10">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-400 mb-4">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <span className="text-gray-600">/</span>
              <Link to="/villages" className="hover:text-white transition-colors">Villages</Link>
              <span className="text-gray-600">/</span>
              <span className="text-amber-400 font-bold">भावी सरपंच डायरेक्टरी</span>
            </div>

            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-950/70 text-amber-300 border border-amber-800/60 text-xs font-bold mb-3 backdrop-blur-md">
                  <Vote className="w-3.5 h-3.5" />
                  <span>ग्राम पंचायत चुनाव जनमत &bull; Future Sarpanch Directory</span>
                </div>

                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white mb-3">
                  गाँव के भावी सरपंच <span className="text-amber-400">प्रत्याशी</span>
                </h1>
                
                <p className="text-gray-300 text-sm sm:text-base max-w-2xl font-normal leading-relaxed">
                  फतेहाबाद जिले के विभिन्न गाँवों से सरपंच पद के भावी प्रत्याशियों के विज़न, योग्यता व प्राथमिकताओं को जानें और उन्हें अपना स्वतंत्र जनसमर्थन (वोट) दें।
                </p>

                {/* Stats row */}
                <div className="flex flex-wrap items-center gap-3 sm:gap-6 mt-4 text-xs font-bold text-gray-300">
                  <div className="flex items-center gap-1.5 bg-gray-900/80 px-3 py-1.5 rounded-xl border border-gray-800">
                    <Users className="w-4 h-4 text-amber-400" />
                    <span>{candidates.length} पंजीकृत प्रत्याशी</span>
                  </div>
                  <div className="flex items-center gap-1.5 bg-gray-900/80 px-3 py-1.5 rounded-xl border border-gray-800">
                    <ThumbsUp className="w-4 h-4 text-emerald-400" />
                    <span>{totalVotesAcrossAll} कुल जनसमर्थन</span>
                  </div>
                  <div className="flex items-center gap-1.5 bg-gray-900/80 px-3 py-1.5 rounded-xl border border-gray-800">
                    <Award className="w-4 h-4 text-primary-400" />
                    <span>100% निष्पक्ष जनमत</span>
                  </div>
                </div>
              </div>

              {/* Action Button: Apply as candidate */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 shrink-0">
                <button
                  onClick={() => setShowApplyModal(true)}
                  className="px-6 py-3.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-2xl font-black text-sm shadow-xl shadow-amber-950/40 transition-all active:scale-95 flex items-center justify-center gap-2"
                >
                  <PlusCircle className="w-5 h-5" />
                  <span>+ भावी सरपंच प्रत्याशी आवेदन करें</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* FILTERS & SEARCH BAR */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 relative z-20">
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-4 sm:p-5 border border-gray-200 dark:border-gray-800 shadow-lg">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              
              {/* Search input */}
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
                <input
                  type="text"
                  placeholder="प्रत्याशी का नाम या विज़न खोजें..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-xs sm:text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:outline-none"
                />
              </div>

              {/* Block Filter */}
              <div>
                <select
                  value={selectedBlock}
                  onChange={(e) => handleBlockChange(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-xs sm:text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:outline-none font-medium"
                >
                  {availableBlocks.map((b) => (
                    <option key={b} value={b}>
                      {b === 'All Blocks' ? 'सभी ब्लॉक (All Blocks)' : `${b} ब्लॉक`}
                    </option>
                  ))}
                </select>
              </div>

              {/* Village Filter */}
              <div>
                <select
                  value={selectedVillage}
                  onChange={(e) => handleVillageChange(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-xs sm:text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:outline-none font-medium"
                >
                  <option value="">
                    {selectedBlock && selectedBlock !== 'All Blocks'
                      ? `${selectedBlock} के सभी गाँव (${blockVillages.length})`
                      : `गाँव अनुसार फिल्टर (${villages.length} Villages)`}
                  </option>
                  {blockVillages.map((v) => (
                    <option key={v._id} value={v.slug}>
                      {v.name} ({v.block} ब्लॉक)
                    </option>
                  ))}
                </select>
              </div>

              {/* Sort Order */}
              <div>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-xs sm:text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:outline-none font-medium"
                >
                  <option value="votes">सर्वाधिक वोट (Most Votes)</option>
                  <option value="newest">नए पंजीकृत प्रत्याशी (Newest)</option>
                  <option value="name">नाम अनुसार (A-Z)</option>
                </select>
              </div>

            </div>
          </div>
        </div>

        {/* CANDIDATES LIST / GRID */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
          {loading ? (
            <PageLoader message="भावी सरपंच प्रत्याशियों की सूची लोड हो रही है..." />
          ) : filteredCandidates.length === 0 ? (
            <div className="bg-white dark:bg-gray-900 rounded-3xl p-10 sm:p-16 text-center border border-gray-200 dark:border-gray-800 shadow-sm max-w-2xl mx-auto my-8">
              <div className="w-16 h-16 bg-amber-50 dark:bg-amber-950/50 text-amber-600 dark:text-amber-400 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                कोई भावी सरपंच प्रत्याशी नहीं मिला
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
                {selectedVillage 
                  ? 'इस गाँव से अभी तक किसी प्रत्याशी ने पंजीकरण नहीं किया है। क्या आप इस गाँव से भावी सरपंच प्रत्याशी हैं?'
                  : 'चयनित फिल्टर के आधार पर अभी कोई स्वीकृत प्रत्याशी उपलब्ध नहीं है।'}
              </p>
              <button
                onClick={() => setShowApplyModal(true)}
                className="px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-xl text-sm shadow-md transition-all active:scale-95 inline-flex items-center gap-2"
              >
                <PlusCircle className="w-4 h-4" />
                <span>पहला प्रत्याशी आवेदन दर्ज करें</span>
              </button>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4">
              {filteredCandidates.map((candidate, idx) => (
                <div
                  key={candidate._id}
                  onClick={() => setSelectedCandidate(candidate)}
                  className="bg-white dark:bg-gray-900 rounded-2xl sm:rounded-3xl border border-gray-200 dark:border-gray-800 p-4 sm:p-5 shadow-sm hover:shadow-lg hover:border-amber-400/80 dark:hover:border-amber-500/80 transition-all duration-200 cursor-pointer group flex flex-col md:flex-row md:items-center justify-between gap-4"
                >
                  {/* Left: Avatar + Details */}
                  <div className="flex items-start sm:items-center gap-3.5 sm:gap-4 min-w-0 flex-1">
                    {/* Photo / Avatar */}
                    <div className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-2xl overflow-hidden bg-gray-100 dark:bg-gray-800 border-2 border-amber-200 dark:border-amber-900/60 shrink-0 shadow-sm">
                      {candidate.photo ? (
                        <img
                          src={getPhotoUrl(candidate.photo)}
                          alt={candidate.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://ui-avatars.com/api/?name=' + encodeURIComponent(candidate.name) + '&background=f59e0b&color=fff&size=100';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-amber-100 to-amber-200 dark:from-amber-950 dark:to-gray-800 text-amber-700 dark:text-amber-300 font-black text-lg">
                          {candidate.name.charAt(0)}
                        </div>
                      )}
                      {idx === 0 && candidate.votesCount > 0 && (
                        <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-[9px] font-black px-1.5 py-0.5 rounded-full shadow-sm flex items-center">
                          #1
                        </span>
                      )}
                    </div>

                    {/* Candidate Info */}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-base sm:text-lg font-black text-gray-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors truncate">
                          {candidate.name}
                        </h3>
                        {idx === 0 && candidate.votesCount > 0 && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 border border-amber-200/60 dark:border-amber-800/60">
                            शीर्ष समर्थन
                          </span>
                        )}
                      </div>

                      {/* Village and Block */}
                      {candidate.village && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 font-medium flex items-center gap-1 mt-0.5">
                          <MapPin className="w-3.5 h-3.5 text-primary-500 shrink-0" />
                          <span>गाँव {candidate.village.name} ({candidate.village.block} ब्लॉक)</span>
                        </div>
                      )}

                      {/* Short Vision Snippet */}
                      <p className="text-xs text-gray-600 dark:text-gray-300 italic line-clamp-1 mt-1 max-w-2xl">
                        "{candidate.vision}"
                      </p>

                      {/* Mini tags */}
                      <div className="flex flex-wrap items-center gap-2 mt-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                        {candidate.education && (
                          <span className="inline-flex items-center gap-1 font-medium">
                            <GraduationCap className="w-3 h-3 text-primary-500" />
                            {candidate.education}
                          </span>
                        )}
                        {candidate.occupation && (
                          <span className="inline-flex items-center gap-1 font-medium">
                            <Briefcase className="w-3 h-3 text-emerald-500" />
                            {candidate.occupation}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right: Votes Count, Action Buttons & Details trigger */}
                  <div className="flex items-center justify-between md:justify-end gap-2.5 pt-3 md:pt-0 border-t md:border-t-0 border-gray-100 dark:border-gray-800 shrink-0">
                    
                    {/* Support / Vote Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleVote(candidate);
                      }}
                      disabled={votingId === candidate._id}
                      className={`px-3.5 sm:px-4 py-2 rounded-xl font-bold text-xs flex items-center gap-1.5 transition-all active:scale-95 shadow-sm ${
                        candidate.userVoted
                          ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                          : 'bg-amber-50 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800/60 hover:bg-amber-100'
                      }`}
                      title={candidate.userVoted ? 'क्लिक कर समर्थन वापस लें' : 'प्रत्याशी को अपना समर्थन/वोट दें'}
                    >
                      <ThumbsUp className={`w-3.5 h-3.5 ${candidate.userVoted ? 'fill-current' : ''}`} />
                      <span>{candidate.userVoted ? 'समर्थन दिया' : 'समर्थन दें'}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-black ml-0.5 ${
                        candidate.userVoted ? 'bg-emerald-800 text-white' : 'bg-white dark:bg-gray-800 text-amber-900 dark:text-amber-200 shadow-sm'
                      }`}>
                        {candidate.votesCount || 0}
                      </span>
                    </button>

                    {/* WhatsApp Share Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleWhatsAppShare(candidate);
                      }}
                      className="p-2 sm:p-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white transition-all shadow-sm active:scale-95 shrink-0"
                      title="WhatsApp पर शेयर करें"
                    >
                      <Share2 className="w-4 h-4" />
                    </button>

                    {/* View Details Click Trigger Button */}
                    <div className="flex items-center gap-1 text-xs font-bold text-gray-500 dark:text-gray-400 group-hover:text-amber-600 dark:group-hover:text-amber-400 pl-1">
                      <span>विवरण</span>
                      <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* CANDIDATE FULL DETAILS MODAL */}
        {selectedCandidate && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm animate-fadeIn"
            onClick={() => setSelectedCandidate(null)}
          >
            <div
              className="bg-white dark:bg-gray-900 rounded-3xl max-w-xl w-full shadow-2xl border border-gray-200 dark:border-gray-800 overflow-hidden flex flex-col max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-amber-600 to-amber-700 text-white p-5 flex items-center justify-between shrink-0">
                <div>
                  <span className="text-[10px] font-black uppercase tracking-wider bg-white/20 px-2.5 py-0.5 rounded-full">
                    भावी सरपंच प्रत्याशी प्रोफाइल
                  </span>
                  <h3 className="text-xl font-black mt-1">{selectedCandidate.name}</h3>
                  <p className="text-amber-100 text-xs">
                    गाँव {selectedCandidate.village?.name} ({selectedCandidate.village?.block || selectedCandidate.block} ब्लॉक, फतेहाबाद)
                  </p>
                </div>
                <button
                  onClick={() => setSelectedCandidate(null)}
                  className="p-1.5 rounded-full hover:bg-white/20 text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-5 sm:p-6 space-y-4 overflow-y-auto text-xs sm:text-sm">
                
                {/* Photo + Basic Information Card */}
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 p-4 bg-gray-50 dark:bg-gray-800/60 rounded-2xl border border-gray-100 dark:border-gray-800">
                  <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl overflow-hidden bg-gray-200 dark:bg-gray-700 border-2 border-amber-300 dark:border-amber-700 flex-shrink-0 shadow-md">
                    {selectedCandidate.photo ? (
                      <img
                        src={getPhotoUrl(selectedCandidate.photo)}
                        alt={selectedCandidate.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center font-black text-3xl text-amber-600 bg-amber-50">
                        {selectedCandidate.name.charAt(0)}
                      </div>
                    )}
                  </div>

                  <div className="space-y-1.5 text-center sm:text-left flex-1 min-w-0">
                    <h4 className="text-lg font-black text-gray-900 dark:text-white">
                      {selectedCandidate.name}
                    </h4>
                    <div className="text-xs text-primary-600 dark:text-primary-400 font-bold flex items-center justify-center sm:justify-start gap-1">
                      <MapPin className="w-3.5 h-3.5" />
                      <span>गाँव {selectedCandidate.village?.name} ({selectedCandidate.village?.block || selectedCandidate.block} ब्लॉक)</span>
                    </div>

                    {/* Quick Contact Buttons */}
                    <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 pt-1.5">
                      {selectedCandidate.phone && (
                        <a
                          href={`tel:${selectedCandidate.phone}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl border border-gray-200 dark:border-gray-600 font-bold text-xs hover:border-primary-500 shadow-sm"
                        >
                          <Phone className="w-3.5 h-3.5 text-primary-600" />
                          <span>{selectedCandidate.phone}</span>
                        </a>
                      )}

                      {(selectedCandidate.whatsapp || selectedCandidate.phone) && (
                        <a
                          href={`https://wa.me/91${(selectedCandidate.whatsapp || selectedCandidate.phone).replace(/\D/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 rounded-xl border border-emerald-200 dark:border-emerald-800 font-bold text-xs hover:bg-emerald-100"
                        >
                          <MessageCircle className="w-3.5 h-3.5 text-emerald-600" />
                          <span>WhatsApp</span>
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {/* Details Pills: Age, Education, Occupation */}
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-2.5 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-800">
                    <div className="text-[10px] text-gray-400 font-semibold">आयु (Age)</div>
                    <div className="text-xs sm:text-sm font-bold text-gray-900 dark:text-white mt-0.5">
                      {selectedCandidate.age ? `${selectedCandidate.age} वर्ष` : '-'}
                    </div>
                  </div>
                  <div className="p-2.5 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-800">
                    <div className="text-[10px] text-gray-400 font-semibold">शिक्षा (Education)</div>
                    <div className="text-xs sm:text-sm font-bold text-gray-900 dark:text-white mt-0.5 truncate">
                      {selectedCandidate.education || '-'}
                    </div>
                  </div>
                  <div className="p-2.5 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-800">
                    <div className="text-[10px] text-gray-400 font-semibold">व्यवसाय (Occupation)</div>
                    <div className="text-xs sm:text-sm font-bold text-gray-900 dark:text-white mt-0.5 truncate">
                      {selectedCandidate.occupation || '-'}
                    </div>
                  </div>
                </div>

                {/* Electoral Vision / Manifesto */}
                <div>
                  <h4 className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                    चुनावी विज़न व संकल्प:
                  </h4>
                  <div className="p-4 bg-amber-50/70 dark:bg-gray-800 rounded-2xl border border-amber-200/70 dark:border-gray-700 text-xs sm:text-sm text-gray-800 dark:text-gray-200 leading-relaxed italic">
                    "{selectedCandidate.vision}"
                  </div>
                </div>

                {/* Key Priorities List */}
                {selectedCandidate.keyPriorities && selectedCandidate.keyPriorities.length > 0 && (
                  <div>
                    <h4 className="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-2">
                      गाँव विकास की मुख्य प्राथमिकताएं:
                    </h4>
                    <div className="space-y-1.5">
                      {selectedCandidate.keyPriorities.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex items-start gap-2 p-2.5 rounded-xl bg-emerald-50/50 dark:bg-emerald-950/30 border border-emerald-100 dark:border-emerald-900/40 text-xs text-gray-800 dark:text-gray-200"
                        >
                          <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              </div>

              {/* Modal Footer: Support Vote & Share */}
              <div className="p-4 bg-gray-50 dark:bg-gray-850 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between gap-3 shrink-0">
                <div className="text-xs font-bold text-gray-600 dark:text-gray-300">
                  कुल जनसमर्थन: <span className="text-purple-600 dark:text-purple-400 font-black text-sm">{selectedCandidate.votesCount || 0} वोट</span>
                </div>

                <div className="flex items-center gap-2">
                  {/* Support Vote Button in Modal */}
                  <button
                    onClick={() => handleVote(selectedCandidate)}
                    disabled={votingId === selectedCandidate._id}
                    className={`px-4 sm:px-5 py-2.5 rounded-xl font-bold text-xs sm:text-sm flex items-center gap-2 transition-all active:scale-95 shadow-md ${
                      selectedCandidate.userVoted
                        ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                        : 'bg-amber-600 hover:bg-amber-700 text-white'
                    }`}
                  >
                    <ThumbsUp className={`w-4 h-4 ${selectedCandidate.userVoted ? 'fill-current' : ''}`} />
                    <span>{selectedCandidate.userVoted ? 'समर्थन दिया (Voted)' : 'समर्थन दें (Vote)'}</span>
                  </button>

                  {/* Share */}
                  <button
                    onClick={() => handleWhatsAppShare(selectedCandidate)}
                    className="p-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white transition-all shadow-sm active:scale-95"
                    title="WhatsApp पर शेयर करें"
                  >
                    <Share2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

            </div>
          </div>
        )}

        {/* APPLY MODAL */}
        <ApplyBhaviSarpanchModal
          isOpen={showApplyModal}
          onClose={() => setShowApplyModal(false)}
          onSuccess={() => {
            fetchCandidates();
          }}
        />

        {/* LOGIN MODAL PROMPT */}
        {showLoginModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn"
            onClick={() => setShowLoginModal(false)}
          >
            <div
              className="bg-white dark:bg-gray-900 rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-gray-200 dark:border-gray-800 text-center relative"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-14 h-14 bg-amber-100 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Lock className="w-7 h-7" />
              </div>

              <h3 className="text-xl font-black text-gray-900 dark:text-white mb-2">
                लॉगिन आवश्यक है (Login Required)
              </h3>

              <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
                प्रत्याशी <strong>{loginTargetName}</strong> को अपना जनसमर्थन/वोट देने के लिए कृपया पहले लॉगिन करें।
                <br />
                <span className="text-xs text-gray-400 mt-1 block">
                  ताकि चुनाव जनमत 100% प्रामाणिक व निष्पक्ष बना रहे।
                </span>
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to={`/login?redirect=/bhavi-sarpanch`}
                  className="w-full sm:w-auto px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-bold text-sm shadow-md transition-all flex items-center justify-center gap-2"
                >
                  <LogIn className="w-4 h-4" />
                  लॉगिन करें (Login)
                </Link>
                <button
                  type="button"
                  onClick={() => setShowLoginModal(false)}
                  className="w-full sm:w-auto px-5 py-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-semibold text-sm transition-all"
                >
                  बंद करें (Close)
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </>
  );
}
